import React, { useRef, useEffect, useContext, useState, useCallback } from "react";
import Player from "@vimeo/player";
import { GlobalContext } from "../../context";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { database } from "../../firebase";
import useInterval from "../../components/Generic/useInterval";

function isVimeoUrl(url) {
  return url && url.includes("vimeo.com");
}

function buildVimeoEmbedUrl(url) {
  if (!url) return null;
  if (url.includes("player.vimeo.com/video/")) return url.replace(/&amp;/g, "&");
  const match = url.match(/vimeo\.com\/(\d+)(?:\/([a-f0-9]+))?/);
  if (match) {
    const id = match[1];
    const hash = match[2];
    let embed = `https://player.vimeo.com/video/${id}?badge=0&autopause=0&player_id=0&app_id=58479`;
    if (hash) embed += `&h=${hash}`;
    return embed;
  }
  return url;
}

export default function VideoPlayer({ uuid, lessonId, videoUrl }) {
  const [player, setPlayer]       = useState(null);
  const [buffering, setBuffering] = useState(false);
  const iframeRef = useRef(null);
  const videoRef  = useRef(null);

  const { authState: { data } } = useContext(GlobalContext);
  const user = data?.user;

  const vimeo    = isVimeoUrl(videoUrl);
  const embedUrl = vimeo ? buildVimeoEmbedUrl(videoUrl) : null;

  /* ── Vimeo SDK ─────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!vimeo || !iframeRef.current) return;
    const p = new Player(iframeRef.current);
    setPlayer(p);
    p.on("bufferstart", () => setBuffering(true));
    p.on("bufferend",   () => setBuffering(false));
    p.on("play",        () => setBuffering(false));
    return () => { p.destroy().catch(() => {}); setPlayer(null); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vimeo, embedUrl]);

  /* ── Restore saved timestamp ───────────────────────────────────────────── */
  const restoreProgress = useCallback(async () => {
    const userId = user?.id?.toString();
    if (!userId || !lessonId) return;
    try {
      const snap = await getDoc(doc(database, userId, lessonId.toString()));
      const ts = snap.exists() ? snap.data()?.video_timestamp : null;
      if (!ts) return;
      if (vimeo && player)         player.setCurrentTime(ts).catch(() => {});
      else if (videoRef.current)   videoRef.current.currentTime = ts;
    } catch (e) { console.error("Error restoring progress:", e); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player, lessonId, videoUrl, vimeo]);

  useEffect(() => { restoreProgress(); }, [restoreProgress]);

  /* ── Save progress every 5 s ───────────────────────────────────────────── */
  const saveProgress = async (progress) => {
    if (!user?.id || !lessonId) return;
    try {
      await setDoc(doc(database, user.id.toString(), lessonId.toString()), {
        user_id: user.id,
        course_id: uuid,
        lesson_id: lessonId,
        video_timestamp: progress,
      });
    } catch (_) {}
  };

  useInterval(() => {
    if (vimeo) {
      if (player) player.getCurrentTime().then(saveProgress).catch(() => {});
    } else {
      if (videoRef.current) saveProgress(videoRef.current.currentTime);
    }
  }, 5000);

  /* ── Shared wrapper — padding-bottom 16:9 trick ────────────────────────── */
  const shell = (inner) => (
    <div className="w-full bg-black rounded-lg overflow-hidden shadow-lg"
      style={{ position: "relative", paddingBottom: "56.25%", height: 0 }}>
      <div style={{ position: "absolute", inset: 0 }}>{inner}</div>
      {buffering && (
        <div style={{ position: "absolute", inset: 0 }}
          className="flex items-center justify-center bg-black/30 pointer-events-none z-10">
          <div className="w-10 h-10 rounded-full border-4 border-white/30 border-t-white animate-spin" />
        </div>
      )}
    </div>
  );

  if (vimeo) {
    return shell(
      <iframe
        ref={iframeRef}
        src={embedUrl}
        frameBorder="0"
        allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
        allowFullScreen
        className="w-full h-full"
        title="Course Video"
      />
    );
  }

  return shell(
    <video
      ref={videoRef}
      src={videoUrl}
      controls
      controlsList="nodownload"
      preload="metadata"
      playsInline
      className="w-full h-full"
      onWaiting={() => setBuffering(true)}
      onCanPlay={() => setBuffering(false)}
      onPlaying={() => setBuffering(false)}
    />
  );
}
